import {
  PhaseShiftElementsRequest,
  PhaseCreate,
  PhaseSplitRequest,
  PhaseUpdate,
  Project,
  isMilestoneChildInProgress,
  isMilestoneChildInProgressOrDone,
  isUserPhaseResponsibleOrAbove,
  validateInsertPhaseBeforeRequest,
  validatePhaseShiftElementsRequest,
  validateRemovePhaseRequest,
  validateSplitPhaseRequest,
  validateUpdatePhaseRequest,
  validateCompressInPhaseRequest,
} from '@beeboard/common';
import { defineStore } from 'pinia';
import { ItemMode, ItemState } from '@/common';
import { useMilestoneProjectStore } from './milestoneProjectStore';
import { usePhaseProjectStore } from './phaseProjectStore';
import { useProfileStore } from './profileStore';
import { useProjectStore } from './projectStore';

export const usePhaseProjectRulesStore = defineStore('phaseProjectRulesStore', {
  getters: {
    user() {
      return useProfileStore().profile;
    },

    project() {
      return (projectId: string): Project => {
        const project = useProjectStore().project(projectId);

        return project;
      };
    },

    phase() {
      return (projectId: string, phaseId: string) => {
        const phaseData = usePhaseProjectStore().phase(projectId, phaseId);

        return phaseData;
      };
    },

    milestone() {
      return (projectId: string, milestoneId: string) => {
        const milestone = useMilestoneProjectStore().milestone(
          projectId,
          milestoneId,
        );

        return milestone;
      };
    },

    projectMembers() {
      return (projectId: string) => {
        const project = useProjectStore().projectMembers(projectId);

        return project;
      };
    },
  },

  actions: {
    userPhaseResponsibleOrAbove(projectId: string, phaseId: string) {
      const project = this.project(projectId);

      return isUserPhaseResponsibleOrAbove(
        this.user.id,
        this.project(projectId),
        phaseId,
        project.editor_ids,
      );
    },

    isPhaseDone(projectId: string, phaseId: string) {
      return this.phase(projectId, phaseId).state === 'done';
    },

    isChildInProgressOrDone(projectId: string, phaseId: string) {
      return isMilestoneChildInProgressOrDone(
        this.project(projectId),
        this.phase(projectId, phaseId),
      );
    },

    isChildInProgress(projectId: string, phaseId: string) {
      const phase = this.phase(projectId, phaseId);

      return isMilestoneChildInProgress(
        this.project(projectId),
        this.milestone(projectId, phase.final_milestone.id),
        phase,
      );
    },

    isStateDisabled(
      fullState: ItemMode | string,
      phaseId: string,
      projectId: string,
    ): boolean {
      const phase = this.phase(projectId, phaseId);

      const toState = fullState.split('-')[0] as ItemState;
      const fromState = phase.state;
      const cannotReturnToPlannedOrUnPlanned =
        (toState === 'planned' || toState === 'unplanned') &&
        this.isChildInProgressOrDone(projectId, phaseId);

      return (
        (fromState === 'unplanned' && toState !== 'planned') ||
        (fromState !== 'planned' && toState === 'unplanned') ||
        cannotReturnToPlannedOrUnPlanned
      );
    },

    isPhaseActionsDisabled(projectId: string, phaseId: string): boolean {
      return !this.userPhaseResponsibleOrAbove(projectId, phaseId);
    },

    validateInsertPhaseBeforeRequest(
      projectId: string,
      before: string | undefined,
      createPhaseRequest: PhaseCreate,
    ) {
      return validateInsertPhaseBeforeRequest(
        this.project(projectId),
        before,
        createPhaseRequest,
        this.projectMembers(projectId),
      );
    },

    validateUpdatePhaseRequest(
      projectId: string,
      phaseId: string,
      updatePhaseRequest: PhaseUpdate,
    ) {
      return validateUpdatePhaseRequest(
        this.project(projectId),
        updatePhaseRequest,
        phaseId,
        this.projectMembers(projectId),
      );
    },

    validateRemovePhaseRequest(projectId: string, phaseId: string) {
      return validateRemovePhaseRequest(this.project(projectId), phaseId);
    },

    validateSplitPhaseRequest(
      projectId: string,
      phaseId: string,
      splitPhaseRequest: PhaseSplitRequest,
    ) {
      return validateSplitPhaseRequest(
        this.project(projectId),
        phaseId,
        splitPhaseRequest,
        this.projectMembers(projectId),
      );
    },

    validateApplySpaceToolRequest(
      projectId: string,
      phaseId: string,
      shiftElementsRequest: PhaseShiftElementsRequest,
    ) {
      return validatePhaseShiftElementsRequest(
        this.project(projectId),
        shiftElementsRequest,
        phaseId,
      );
    },

    validateApplyCompressToolRequest(
      projectId: string,
      phaseId: string,
      deadline: Date,
      direction: 'right' | 'left',
      fixedMilestoneIds: string[] = [],
      compressDays?: number,
    ) {
      return validateCompressInPhaseRequest(
        this.project(projectId),
        phaseId,
        deadline,
        direction,
        fixedMilestoneIds,
        compressDays,
      );
    },
  },
});
